import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { formatearNombreRol, getApellidos } from "../utils/formatters";

const AssignInstitutionDialog = ({
  open,
  onClose,
  onSave,
  usuario,
  formData,
  onChange,
  // Datos para los selects
  rolesAsignables,
  regiones,
  provincias,
  distritos,
  instituciones,
  // Estados de carga
  loading,
  canSave,
  mode = "create",
}) => {
  const isEdit = mode === "edit";
  
  // Determinar si se deben mostrar los campos de ubicación
  const requiereUbicacion = 
    ["director", "profesor"].includes(formData.rol_nombre?.toLowerCase());

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          backgroundColor: "#5b6b8a",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: "Montserrat-Bold",
          fontSize: "1rem",
          px: 3,
          py: 2,
        }}
      >
        {isEdit
          ? `Editar vínculo de ${usuario?.nombres || ""} ${getApellidos(usuario)}`
          : `Asignar institución a ${usuario?.nombres || ""} ${getApellidos(usuario)}`}
        <IconButton onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 3, py: 3 }}>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Selector de Rol */}
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Rol"
              name="rol_id"
              value={formData.rol_id}
              onChange={onChange}
            >
              {rolesAsignables.map((rol) => (
                <MenuItem key={rol.id} value={rol.id}>
                  {formatearNombreRol(rol.nombre)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {isEdit && (
            <Grid item xs={12}>
              <Typography
                sx={{
                  p: 0,
                  fontFamily: "Montserrat-Regular",
                  fontSize: "0.84rem",
                  color: "#64748b",
                }}
              >
                Cambiar la institución o el rol actualizará el vínculo actual.
              </Typography>
            </Grid>
          )}

          {requiereUbicacion && (
            <>
              {/* Región */}
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Región"
                  name="region_id"
                  value={formData.region_id}
                  onChange={onChange}
                  disabled={loading.regiones}
                >
                  {regiones.map((reg) => (
                    <MenuItem key={reg.id || reg.ubigeo} value={reg.id || reg.ubigeo}>
                      {reg.nombre}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Provincia */}
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Provincia"
                  name="provincia_id"
                  value={formData.provincia_id}
                  onChange={onChange}
                  disabled={!formData.region_id || loading.provincias}
                >
                  {provincias.map((prov) => (
                    <MenuItem key={prov.id || prov.ubigeo} value={prov.id || prov.ubigeo}>
                      {prov.nombre}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Distrito */}
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Distrito"
                  name="distrito_id"
                  value={formData.distrito_id}
                  onChange={onChange}
                  disabled={!formData.provincia_id || loading.distritos}
                >
                  {distritos.map((dist) => (
                    <MenuItem key={dist.id || dist.ubigeo} value={dist.id || dist.ubigeo}>
                      {dist.nombre}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Institución */}
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Institución educativa"
                  name="institucion_id"
                  value={formData.institucion_id}
                  onChange={onChange}
                  disabled={!formData.distrito_id || loading.instituciones}
                >
                  {instituciones.map((inst) => (
                    <MenuItem key={inst.id} value={inst.id}>
                      {inst.nombre} ({inst.nivel_modalidad})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={btnStyles}>
          Cancelar
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={!canSave || Object.values(loading).some(v => v)}
          sx={{
            ...btnStyles,
            backgroundColor: "#5b6b8a",
            "&:hover": { backgroundColor: "#495875" },
          }}
        >
          {Object.values(loading).some(v => v) ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            isEdit ? "Guardar cambios" : "Guardar asignación"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const btnStyles = {
  textTransform: "none",
  fontFamily: "Montserrat-Medium",
  borderRadius: "8px",
  px: 2.5,
  boxShadow: "none",
};

export default AssignInstitutionDialog;
